import { defineAsyncComponent } from 'vue'

import Popover from 'src/components/popover/popover.vue'
import StatusBookmarkFolderMenu from 'src/components/status_bookmark_folder_menu/status_bookmark_folder_menu.vue'
import EmojiPicker from '../emoji_picker/emoji_picker.vue'

import { useInstanceStore } from 'src/stores/instance.js'
import { useInstanceCapabilitiesStore } from 'src/stores/instance_capabilities.js'
import { useMergedConfigStore } from 'src/stores/merged_config.js'
import { useStatusesStore } from 'src/stores/statuses.js'

import { compactCount } from 'src/services/compact_count/compact_count.js'
import localeService from 'src/services/locale/locale.service.js'
import { reactionSupport } from 'src/services/reaction_support/reaction_support.js'

export default {
  props: [
    'button',
    'status',
    'extra',
    'funcArg',
    'getComponent',
    'doAction',
    'outerClose',
    // a pill beside the reactions: the count inside the button
    'chip',
  ],
  components: {
    StatusBookmarkFolderMenu,
    EmojiPicker,
    Popover,
    UserListPopover: defineAsyncComponent(
      () => import('src/components/user_list_popover/user_list_popover.vue'),
    ),
  },
  data: () => ({
    animationState: false,
    // the reaction picker exists from its first opening on
    pickerWanted: false,
  }),
  computed: {
    buttonClass() {
      return [
        this.button.name + '-button',
        {
          '-with-extra': this.button.name === 'bookmark',
          '-extra': this.extra,
          '-quick': !this.extra,
        },
      ]
    },
    hideCustomEmoji() {
      return !useInstanceCapabilitiesStore()
        .pleromaCustomEmojiReactionsAvailable
    },
    hidePostStats() {
      return useMergedConfigStore().mergedConfig.hidePostStats
    },
    counterShown() {
      return !this.hidePostStats && this.button.counter?.(this.funcArg) > 0
    },
    peopleAvailable() {
      return (
        !!this.button.people &&
        (!this.button.peopleAvailable ||
          this.button.peopleAvailable(this.funcArg))
      )
    },
    people() {
      return this.button.people?.(this.funcArg)
    },
    buttonInnerClass() {
      return [
        this.button.name + '-button',
        {
          'main-button': this.extra,
          'button-unstyled': !this.extra,
          '-active': this.button.active?.(this.funcArg),
          disabled: this.button.interactive
            ? !this.button.interactive(this.funcArg)
            : false,
        },
      ]
    },
    // the author's server shows no reactions, or no custom ones
    reactionNotice() {
      const user = this.status.user
      if (!user || user.is_local) return null
      const server = user.instance?.name ?? user.screen_name?.split('@')[1]
      const support = reactionSupport(user.instance)
      if (support === 'none') {
        return this.$t('emoji.reactions_not_shown', { server })
      }
      // custom emoji are hidden in this picker anyway
      if (support === 'unicode' && !this.hideCustomEmoji) {
        return this.$t('emoji.custom_reactions_not_shown', { server })
      }
      return null
    },
    remoteInteractionLink() {
      return useInstanceStore().getRemoteInteractionLink({
        statusId: this.status.id,
      })
    },
  },
  methods: {
    // 1.2K rather than 1234, so the count fits beside the icon
    shortCount(count) {
      return compactCount(
        count,
        localeService.internalToBrowserLocale(this.$i18n.locale),
      )
    },
    addReaction(event) {
      const emoji = event.insertion
      const existingReaction = this.status.emoji_reactions.find(
        (r) => r.name === emoji,
      )
      if (existingReaction?.me) {
        useStatusesStore().unreactWithEmoji(this.status.id, emoji)
      } else {
        useStatusesStore().reactWithEmoji(this.status.id, emoji)
      }
    },
    onShowEmojiPicker() {
      this.$emit('emojiPickerShown', true)
    },
    onHideEmojiPicker() {
      this.$emit('emojiPickerShown', false)
    },
    doActionWrap(
      button,
      close = () => {
        /* no-op */
      },
    ) {
      if (
        this.button.interactive ? !this.button.interactive(this.funcArg) : false
      )
        return
      if (button.name === 'emoji') {
        this.pickerWanted = true
        this.$nextTick(() => this.$refs.picker.togglePicker())
      } else {
        this.animationState = true
        this.getComponent(button) === 'button' && this.doAction(button)
        setTimeout(() => {
          this.animationState = false
        }, 500)
        if (!this.button.dropdown) close()
      }
    },
  },
}
